import type { ReactNode } from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import * as Ariakit from '@ariakit/react';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';

import { Badge } from '../Badge/Badge';
import { Box } from '../Box/Box';
import type { ButtonProps } from '../Button/Button';
import { Button } from '../Button/Button';
import { IconSolidCheveronDown, IconSolidCheveronUp } from '../icons';
import type { OptionProps, SelectProps } from '../Select/Select';
import { Select as UISelect } from '../Select/Select';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

import type { Variants } from './styles.css';
import { inputNumber, inputNumberButton, inputNumberDivider, inputNumberIcon, inputVariants } from './styles.css';

type FormComponent = React.FC<Props> & {
  Input: typeof Input;
  Error: typeof Error;
  Submit: typeof Submit;
  Select: typeof Select;
  Option: typeof Option;
  NamedSection: typeof NamedSection;
  Label: typeof Label;
  useStore: typeof Ariakit.useFormStore;
  useContext: typeof Ariakit.useFormContext;
};

interface LabelProps {
  label: string;
  name: InputProps['name'];
  disabled?: boolean;
  tag?: ReactNode;
  optional?: boolean;
}

export const Label: React.FC<LabelProps> = ({ label, name, tag, optional, disabled }) => (
  <Box display="flex" flexDirection="row" gap="6">
    {label && (
      <Box display="flex" alignItems="center" gap="6" style={{ height: 16 }}>
        <Ariakit.FormLabel name={name}>
          <Text
            userSelect="none"
            size="xSmall"
            weight="medium"
            color={disabled ? 'secondaryContentOnDisabled' : 'weak'}
          >
            {label}
          </Text>
        </Ariakit.FormLabel>
        {tag}
      </Box>
    )}
    {optional && <Badge shape="basic" size="small" label="Optional" />}
  </Box>
);

type HasLabel = {
  name: Ariakit.FormInputProps['name'];
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  tag?: ReactNode;
  icon?: ReactNode;
};
export const NamedSection = ({
  children,
  label,
  disabled,
  name,
  tag,
  icon,
  optional = false,
}: React.PropsWithChildren<HasLabel>) =>
  label ? (
    <Box display="flex" flexDirection="column" width="full" gap="4">
      <Box display="flex" flexDirection="row" gap="6" alignItems="center">
        {label && <Label label={label} name={name} tag={tag} disabled={disabled} />}
        {optional && <Badge shape="basic" size="small" label="Optional" />}
        {icon}
      </Box>
      {children}
    </Box>
  ) : (
    <>{children}</>
  );

type Props = Ariakit.FormProps & Pick<Ariakit.FormProviderProps, 'defaultValues' | 'store'>;

export const Form: FormComponent = ({
  children,
  defaultValues,
  store,
  noValidate = false, // use browser validation by default
  ...props
}: Props) => (
  <Ariakit.FormProvider defaultValues={defaultValues} store={store}>
    <Ariakit.Form noValidate={noValidate} {...props} role="form">
      {children}
    </Ariakit.Form>
  </Ariakit.FormProvider>
);

export const Error = ({ ...props }: Ariakit.FormErrorProps) => (
  <Ariakit.FormError {...props} render={<Box color="bad" />} />
);

export const Submit: React.FC<Ariakit.FormSubmitProps & ButtonProps> = (props) => (
  <Ariakit.FormSubmit render={<Button />} {...props} />
);

export type InputProps = Omit<Ariakit.FormInputProps, 'size'> &
  Variants &
  HasLabel & {
    cssClass?: ClassValue | ClassValue[];
    labelTag?: LabelProps['tag'];
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, labelTag, icon, cssClass, size, collapsed, truncate, outline, name, rounded, ...props }, ref) => {
    const _ref = useRef<HTMLInputElement>(null);
    const inputRef = (ref ?? _ref) as React.MutableRefObject<HTMLInputElement>;
    useEffect(() => {
      if (collapsed && inputRef.current) {
        inputRef.current.blur();
      }
    }, [collapsed]);
    if (inputRef.current && inputRef.current.value) {
      collapsed = false;
    }

    const isNumber = props.type === 'number';
    if (isNumber) {
      props.step = props.step ?? 1;
    }

    const emitChange = () => {
      const event = new Event('change', {
        bubbles: true,
      });
      inputRef.current.dispatchEvent(event);
    };

    return (
      <NamedSection label={label} name={name} icon={icon} tag={labelTag} disabled={props.disabled}>
        <Box position="relative" flex="stretch">
          <Ariakit.FormInput
            ref={inputRef}
            name={name}
            className={clsx(
              inputVariants({
                size,
                collapsed,
                outline,
                truncate,
                rounded,
              }),
              cssClass,
              {
                [inputNumber]: isNumber,
              },
            )}
            {...props}
          />

          {isNumber && (
            <Box display="flex" position="absolute" flexDirection="column" style={{ top: 0, right: 0, bottom: 0 }}>
              <button
                type="button"
                className={inputNumberButton}
                onClick={(e) => {
                  const steps = e.shiftKey ? 10 : 1;
                  inputRef.current?.stepUp(steps);
                  emitChange();
                }}
              >
                <IconSolidCheveronUp size="16" className={inputNumberIcon} />
              </button>
              <Box cssClass={inputNumberDivider} />
              <button
                type="button"
                className={inputNumberButton}
                onClick={(e) => {
                  const steps = e.shiftKey ? 10 : 1;
                  inputRef.current?.stepDown(steps);
                  emitChange();
                }}
              >
                <IconSolidCheveronDown size="16" className={inputNumberIcon} />
              </button>
            </Box>
          )}
        </Box>
      </NamedSection>
    );
  },
);

type FormSelectProps = Ariakit.FormInputProps &
  React.PropsWithChildren<HasLabel> &
  Omit<SelectProps, 'name' | 'onChange'>;

export const Select = ({
  children,
  label = '',
  tag,
  optional,
  name,
  options,
  creatable,
  disabled,
  filterable,
  checkType,
  defaultValue,
  displayMode,
  loading,
  trigger,
  onValueChange,
  onCreate,
  renderValue,
  ...props
}: FormSelectProps) => {
  const form = Ariakit.useFormContext()!;
  const value = props.value ?? form.useValue(name);

  return (
    <Stack direction="column" gap="4">
      {label.trim() !== '' && <Label label={label} name={name} tag={tag} optional={optional} disabled={disabled} />}
      <Ariakit.FormInput
        name={name}
        render={
          <UISelect
            value={value}
            options={options}
            creatable={creatable}
            disabled={disabled}
            filterable={filterable}
            checkType={checkType}
            defaultValue={defaultValue}
            displayMode={displayMode}
            loading={loading}
            trigger={trigger}
            renderValue={renderValue}
            onCreate={onCreate}
            onValueChange={(option) => {
              form.setValue(name, option.value);

              if (onValueChange) {
                onValueChange(option);
              }
            }}
          />
        }
        {...props}
      >
        {children}
      </Ariakit.FormInput>
    </Stack>
  );
};

export const Option: React.FC<OptionProps> = ({ children, ...props }) => (
  <UISelect.Option {...props}>{children}</UISelect.Option>
);

Form.Input = Input;
Form.Error = Error;
Form.Submit = Submit;
Form.Label = Label;
Form.Select = Select;
Form.Option = Option;
Form.NamedSection = NamedSection;
Form.useStore = Ariakit.useFormStore;
Form.useContext = Ariakit.useFormContext;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export declare type FormState<T extends Record<string, any>> = Ariakit.FormStore<T>;
